// The creators who applied to a campaign the brand picks for, on the
// campaign page. Since 2026-10-12 (Brúnó: brands decide by the photo first,
// then by what other brands thought) each one is a card with a large photo,
// their level, their rating and the note they wrote; the better creators come
// first, as the server orders them (data/job-people.ts), twelve to a page. A
// click opens their profile: photos, ratings, earlier work and accounts. A
// pick gives the creator a spot and tells them; when the campaign fills, the
// ones still waiting are told it was not them. Its own tab, Invite creators
// (2026-10-12), lists the creators the campaign is open to who have not
// applied, the same way: an invite sends them the campaign as they would see
// it to apply, nothing of the brand's own words, and 48 hours to accept. The
// campaign's header and the overview's to-do list lead there too. A pick or
// an invite sets aside the creator's videos from the brand's credit (Dani,
// 2026-10-14); short of it, credit-dialog.tsx says what to add.

import {useCallback, useEffect, useRef, useState} from 'react';
import type {ReactNode} from 'react';
import type {BrandApplicant, BrandCampaign, BrandCandidate, BrandRatingSummary} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';
import {PersonPhoto, personInitials} from '../shared/person/Person';
import {BrandApiError, creatorPicture, describe, fetchCandidates, inviteCreator, pickApplicant, revokeCreatorInvite} from './api';
import {useCreditGate} from './credit-dialog';
import {useCreatorProfile} from './creator-profile';
import {money} from './format';
import {InfoTip} from './info-tip';
import {usd} from './plan-credit';
import {RatingBadge} from './stars';
import {useBrand} from './store';
import './applicants.css';

const LEVELS: Record<BrandApplicant['level'], string> = {starter: 'Starter', rising: 'Rising', pro: 'Pro', elite: 'Elite'};
const BANDS: Record<string, string> = {'0-500': 'under 500 followers', '500-2k': '500–2K followers', '2k-10k': '2K–10K followers', '10k-50k': '10K–50K followers', '50k+': '50K+ followers'};
const STATUS: Record<BrandApplicant['status'], string> = {applied: 'Waiting', picked: 'Picked', not_picked: 'Not picked'};
/** The first page: enough to choose from without scrolling through everyone. */
const PAGE = 12;

const WHEN = new Intl.DateTimeFormat('en-US', {weekday: 'short', hour: 'numeric', minute: '2-digit'});

interface CardPerson {
  account_id: string;
  name: string;
  country: string;
  level: BrandApplicant['level'];
  followers: BrandApplicant['followers'];
  photo_file_id: string | null;
  rating: BrandRatingSummary;
}

/** One creator as a card: the photo large, then their level, rating, country and followers. */
function CreatorCard({person, className = '', pill, note, extra, onOpen, children}: {
  person: CardPerson; className?: string; pill?: ReactNode; note?: string; extra?: ReactNode; onOpen(): void; children: ReactNode;
}) {
  const ranked = person.level === 'pro' || person.level === 'elite';
  return (
    <article className={`applicant-card ${className}`}>
      <button className="applicant-photo" type="button" onClick={onOpen} aria-label={`Open ${person.name}’s profile`}>
        <PersonPhoto sources={creatorPicture(person, 480)} fallback={<span className="applicant-initials">{personInitials(person.name)}</span>} />
        {ranked ? <span className={`applicant-level ${person.level}`}>{LEVELS[person.level]}</span> : null}
        {pill}
      </button>
      <div className="applicant-body">
        <div className="applicant-name">
          <b>{person.name}</b>
          {person.country ? <small>{person.country}</small> : null}
        </div>
        <RatingBadge rating={person.rating} />
        <small className="applicant-meta">{[ranked ? '' : LEVELS[person.level], BANDS[person.followers] ?? ''].filter(Boolean).join(' · ')}</small>
        {note ? <p className="applicant-note">“{note}”</p> : null}
        {extra}
      </div>
      <div className="applicant-actions">
        <button className="btn btn-light btn-sm" type="button" onClick={onOpen}>Profile</button>
        {children}
      </div>
    </article>
  );
}

/** How many creators the campaign has, and whether a spot is left. */
export function campaignSeats(campaign: BrandCampaign): {picked: number; full: boolean; live: boolean} {
  const picked = campaign.applicants.filter((applicant) => applicant.status === 'picked').length;
  const holding = campaign.participants.filter((participant) => participant.reservation_status && !['released', 'expired', 'cancelled'].includes(participant.reservation_status)).length;
  return {picked, full: campaign.creator_count > 0 && Math.max(picked, holding) >= campaign.creator_count, live: campaign.status === 'active'};
}

/** What each creator sets aside, and what is free: a chip in the head while there is a spot to fill. */
function CreditChip({perCreator, free}: {perCreator: number; free: number}) {
  if (!perCreator) return null;
  return <span className="head-chip"><Icon name="wallet" size={14} /><span><b>{usd(perCreator)}</b> per creator · <b>{usd(free)}</b> free</span>
    <InfoTip text={`Each creator you bring in sets aside ${money(perCreator, 'USD')} of your credit for their videos. ${money(free, 'USD')} is free now.`} /></span>;
}

export function ApplicantsSection({campaign, onInvite}: {campaign: BrandCampaign; onInvite(): void}) {
  const {reload, toast} = useBrand();
  const [busy, setBusy] = useState('');
  const [newest, setNewest] = useState(false);
  const [everyone, setEveryone] = useState(false);
  const profile = useCreatorProfile();
  const credit = useCreditGate(campaign, 'applicants');
  if (campaign.take_mode !== 'apply') return null;
  const {picked, full, live} = campaignSeats(campaign);
  const invitable = live && !full && !campaign.offer;
  const inviteMore = invitable && campaign.applicants.length > 0;
  const sortable = campaign.applicants.length > 1;
  // Who can still be picked, first; then the team; then the ones it was not this time. Within each,
  // the server's order (the better creators first), or the newest first when the brand asks for that.
  const ORDER: Record<BrandApplicant['status'], number> = full ? {picked: 0, applied: 1, not_picked: 2} : {applied: 0, picked: 1, not_picked: 2};
  const applicants = [...campaign.applicants].sort((a, b) => ORDER[a.status] - ORDER[b.status]
    || (newest ? b.applied_at.localeCompare(a.applied_at) : 0));
  const shown = everyone ? applicants : applicants.slice(0, PAGE);

  async function pick(applicant: BrandApplicant) {
    if (!credit.covered(applicant.name, 'pick')) return;
    setBusy(applicant.account_id);
    try {
      await pickApplicant(campaign.id, applicant.account_id);
      toast(`${applicant.name} is in. We let them know.`);
      await reload(true);
    } catch (error) {
      if (!(await credit.refused(error, applicant.name, 'pick'))) toast(describe(error), true);
    } finally {
      setBusy('');
    }
  }

  return (
    <section className="card brand-section">
      <div className="people-head">
        <div className="people-head-main">
          <h2>Applicants<InfoTip text={full
            ? 'The campaign is full; the others heard it was not them this time.'
            : live ? 'Pick who you want to work with. When the campaign is full, the others hear it was not them this time.' : 'You can pick once the campaign is live.'} /></h2>
          <span className="head-chip"><span><b>{picked} / {campaign.creator_count}</b> picked</span></span>
          {live && !full ? <CreditChip perCreator={credit.perCreator} free={credit.free} /> : null}
        </div>
        {inviteMore || sortable ? <div className="people-head-tools">
          {inviteMore ? <button className="btn btn-light btn-sm" type="button" onClick={onInvite}
            title="Invite creators this campaign is open to"><Icon name="send" size={15} />Invite more</button> : null}
          {sortable ? <div className="segmented applicant-sort" role="group" aria-label="Order">
            <button type="button" aria-pressed={!newest} onClick={() => setNewest(false)}>Best first</button>
            <button type="button" aria-pressed={newest} onClick={() => setNewest(true)}>Newest</button>
          </div> : null}
        </div> : null}
      </div>
        {campaign.applicants.length === 0 ? (
          <div className="empty-state">
            <Icon name="user" size={28} />
            <b>No applications yet</b>
            <span>{live ? 'Creators who fit the campaign apply from its brief, with a short note. You can also invite creators yourself.' : 'Applications open once the campaign is live.'}</span>
            {invitable ? <button className="btn btn-g" type="button" onClick={onInvite}><Icon name="send" size={16} />Invite creators</button> : null}
          </div>
        ) : (
          <>
            <div className="applicant-grid">
              {shown.map((applicant) => (
                <CreatorCard key={applicant.account_id} person={applicant} className={applicant.status} note={applicant.note}
                  onOpen={() => profile.open(applicant.account_id)}
                  pill={applicant.status === 'applied' ? null : <span className={`status-pill applicant-${applicant.status}`}>{STATUS[applicant.status]}</span>}>
                  {applicant.status === 'picked' ? <span className="applicant-picked"><Icon name="check" size={15} />Picked</span>
                    : full || !live ? null : (
                      <button className="btn btn-g btn-sm" type="button" disabled={Boolean(busy)} onClick={() => void pick(applicant)}>
                        {busy === applicant.account_id ? 'Picking…' : 'Pick'}{busy === applicant.account_id ? null : <Icon name="check" size={15} />}
                      </button>
                    )}
                </CreatorCard>
              ))}
            </div>
            {applicants.length > shown.length ? <button className="btn btn-light applicant-more" type="button" onClick={() => setEveryone(true)}>
              Show all {applicants.length} applicants<Icon name="chevronDown" size={16} /></button> : null}
          </>
        )}
      {profile.panel}
      {credit.dialog}
    </section>
  );
}

/** The campaign's Invite creators tab, a card of its own. */
export function InviteSection({campaign}: {campaign: BrandCampaign}) {
  const profile = useCreatorProfile();
  const {full, live} = campaignSeats(campaign);
  return (
    <section className="card brand-section">
      <InviteCreators key={campaign.id} campaign={campaign} full={full} live={live} onOpen={profile.open}
        title={<h2>Invite creators<InfoTip text={campaign.access_mode === 'invited'
          ? 'Creators who fit this campaign, the best first. Only the creators you invite see it: an invite sends them the campaign with a notification; they have 48 hours to accept, and accepting takes a spot.'
          : 'Creators this campaign is open to who have not applied, the best first. An invite sends them the campaign as they would see it to apply, with a notification; they have 48 hours to accept, and accepting takes a spot.'} /></h2>} />
      {profile.panel}
    </section>
  );
}

/**
 * Creators the campaign is open to who have not applied, the better ones
 * first, searched by name or handle. An invite sends them the campaign itself;
 * a pending one can be taken back, and a declined one is left alone.
 */
function InviteCreators({campaign, full, live, title, onOpen}: {
  campaign: BrandCampaign; full: boolean; live: boolean; title: ReactNode; onOpen(accountId: string): void;
}) {
  const {toast} = useBrand();
  const credit = useCreditGate(campaign, 'invite');
  const [query, setQuery] = useState('');
  const [list, setList] = useState<BrandCandidate[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [pending, setPending] = useState(0);
  const [limit, setLimit] = useState(20);
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Each request has a number; an answer that is no longer the latest (an older search, or a page
  // asked for before the search changed) is dropped, so the list always matches what is typed.
  const latest = useRef(0);

  const load = useCallback(async (search: string, offset: number) => {
    const request = ++latest.current;
    setError(''); setLoading(true);
    try {
      const page = await fetchCandidates(campaign.id, search, offset);
      if (request !== latest.current) return;
      setList((current) => (offset && current ? [...current, ...page.candidates.filter((item) => !current.some((had) => had.account_id === item.account_id))] : page.candidates));
      setHasMore(page.has_more); setPending(page.pending_invites); setLimit(page.invite_limit);
    } catch (caught) {
      if (request === latest.current) setError(describe(caught));
    } finally {
      if (request === latest.current) setLoading(false);
    }
  }, [campaign.id]);

  useEffect(() => {
    if (!live) return undefined;
    const timer = window.setTimeout(() => void load(query, 0), query ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [query, load, live]);

  async function answer(candidate: BrandCandidate, revoke: boolean) {
    if (!revoke && !credit.covered(candidate.name, 'invite', pending)) return;
    setBusy(candidate.account_id);
    try {
      const result = await (revoke ? revokeCreatorInvite : inviteCreator)(campaign.id, candidate.account_id);
      setList((current) => current?.map((item) => (item.account_id === candidate.account_id ? {...item, invite: result.invite} : item)) ?? null);
      setPending(result.pending_invites);
      toast(revoke ? `Invite to ${candidate.name} cancelled` : `${candidate.name} is invited. They have 48 hours to accept.`);
    } catch (caught) {
      if (!revoke && await credit.refused(caught, candidate.name, 'invite', pending)) return;
      toast(describe(caught), true);
      // Someone who can no longer be invited (applied, joined, out of the audience) leaves the list.
      if (caught instanceof BrandApiError && caught.code === 'not_invitable') void load(query, 0);
    } finally { setBusy(''); }
  }

  if (!live) {
    return <>
      <div className="people-head"><div className="people-head-main">{title}</div></div>
      <div className="empty-state"><Icon name="send" size={28} /><b>Not live yet</b><span>You can invite creators once the campaign is live.</span></div>
    </>;
  }
  return <>
    <div className="people-head">
      <div className="people-head-main">
        {title}
        <span className="head-chip"><span><b>{pending} / {limit}</b> invites waiting</span></span>
        {full ? <span className="head-chip full"><span>Campaign full</span><InfoTip text="There is no spot to invite anyone to." /></span>
          : <CreditChip perCreator={credit.perCreator} free={credit.free} />}
      </div>
      <div className="people-head-tools with-search">
        <label className="table-search people-search"><Icon name="search" size={16} />
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by name or @handle" aria-label="Search creators" /></label>
      </div>
    </div>
    {credit.dialog}
    {error ? <p className="form-alert people-alert">{error}</p> : null}
    {list === null ? (error ? null : <div className="profile-loading"><span className="loader" /></div>) : list.length === 0 ? (
      <div className="empty-state"><Icon name="user" size={28} /><b>{query ? 'Nobody by that name' : 'No one else to invite yet'}</b>
        <span>{query ? 'Try another name or handle.' : 'Every creator this campaign is open to has applied or is already in.'}</span></div>
    ) : <>
      <div className="applicant-grid">
        {list.map((candidate) => {
          const invite = candidate.invite;
          const waiting = invite?.status === 'pending';
          const ours = invite?.by_brand ?? true;
          return (
            <CreatorCard key={candidate.account_id} person={candidate} className={invite?.status === 'declined' ? 'not_picked' : ''}
              onOpen={() => onOpen(candidate.account_id)}
              pill={waiting ? <span className="status-pill applicant-applied">Invited</span>
                : invite?.status === 'declined' ? <span className="status-pill applicant-not_picked">Declined</span> : null}
              extra={waiting && invite.expires_at ? <small className="applicant-deadline">{ours ? 'Invited' : 'Invited by Earn'} · answer by {WHEN.format(new Date(invite.expires_at))}</small> : null}>
              {waiting && !ours ? null : waiting ? (
                <button className="btn btn-light btn-sm" type="button" disabled={Boolean(busy)} onClick={() => void answer(candidate, true)}>
                  {busy === candidate.account_id ? 'Cancelling…' : 'Cancel invite'}</button>
              ) : invite?.status === 'declined' || full ? null : (
                <button className="btn btn-g btn-sm" type="button" disabled={Boolean(busy)} onClick={() => void answer(candidate, false)}>
                  {busy === candidate.account_id ? 'Inviting…' : invite ? 'Invite again' : 'Invite'}{busy === candidate.account_id ? null : <Icon name="send" size={15} />}
                </button>
              )}
            </CreatorCard>
          );
        })}
      </div>
      {hasMore ? <button className="btn btn-light applicant-more" type="button" disabled={loading} onClick={() => void load(query, list.length)}>
        {loading ? 'Loading…' : 'Show more creators'}{loading ? null : <Icon name="chevronDown" size={16} />}</button> : null}
    </>}
  </>;
}
